<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <!-- 検索条件 -->
          <v-row>
            <v-col cols="3">
              <DatePicker
                ref="dateFrom"
                v-model="searchCondition.dateFrom"
                :label="$t('resultExport.label.dateFrom')"
              ></DatePicker>
            </v-col>
            <v-col cols="3">
              <DatePicker
                ref="dateTo"
                v-model="searchCondition.dateTo"
                :label="$t('resultExport.label.dateTo')"
              ></DatePicker>
            </v-col>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.siteCodeList"
                :items="siteItem"
                item-title="code_name"
                item-value="code"
                :label="$t('resultExport.label.site')"
                multiple
                chips
                :clearable="!siteFixed"
                :disabled="siteFixed"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.exportStatus"
                :items="exportStatusItem"
                item-title="label"
                item-value="key"
                :label="$t('resultExport.label.exportStatus')"
              />
            </v-col>
            <v-col cols="9">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.voucherTypeList"
                :items="voucherTypeItem"
                item-title="label"
                item-value="key"
                :label="$t('resultExport.label.voucherType')"
                multiple
                chips
                clearable
              />
            </v-col>
            <v-col cols="3" class="text-right">
              <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
              <v-btn
                theme="dark"
                class="ml-4 px-8 bg-grey-darken-3"
                elevation="2"
                @click.prevent="onSearchClick"
                :loading="isSearching"
                >{{ $t('common.action.search') }}</v-btn
              >
            </v-col>
          </v-row>

          <!-- サマリ -->
          <v-row v-if="summary">
            <v-col cols="12" class="text-grey-darken-1">
              {{ $t('resultExport.label.summary', [summary.targetCount, summary.exportedCount, summary.unexportedCount]) }}
            </v-col>
          </v-row>

          <!-- 一覧 -->
          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        class="re-chk"
                        :checked="isAllSelected"
                        :indeterminate.prop="isPartialSelected"
                        @change="toggleSelectAll($event.target.checked)"
                      />
                    </th>
                    <th>{{ $t('resultExport.label.table.siteCode') }}</th>
                    <th>{{ $t('resultExport.label.table.voucherType') }}</th>
                    <th>{{ $t('resultExport.label.table.voucherNo') }}</th>
                    <th class="text-right">{{ $t('resultExport.label.table.meisaiSu') }}</th>
                    <th class="text-right">{{ $t('resultExport.label.table.suuryoKei') }}</th>
                    <th>{{ $t('resultExport.label.table.confirmedDatetime') }}</th>
                    <th>{{ $t('resultExport.label.table.exportStatus') }}</th>
                    <th>{{ $t('resultExport.label.table.lastExportedAt') }}</th>
                    <th>{{ $t('resultExport.label.table.outputBy') }}</th>
                    <th>{{ $t('resultExport.label.table.output') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="item in groupedRows" :key="item._header ? 'h_' + item.date : voucherKey(item)">
                    <tr v-if="item._header" class="re-group">
                      <td colspan="11">
                        <strong>{{ item.date || '—' }}</strong>
                        <span class="ml-4 text-caption">{{ $t('resultExport.label.groupCount', [item.total, item.unexported]) }}</span>
                      </td>
                    </tr>
                    <tr v-else>
                      <td>
                        <input
                          type="checkbox"
                          class="re-chk"
                          :checked="isSelected(item)"
                          @change="toggleRow(item)"
                        />
                      </td>
                      <td>{{ item.siteCode }}</td>
                      <td>{{ voucherTypeLabel(item.voucherType) }}</td>
                      <td>{{ item.voucherNo }}</td>
                      <td class="text-right">{{ item.meisaiSu }}</td>
                      <td class="text-right">{{ item.suuryoKei }}</td>
                      <td>{{ fmtDateTime(item.confirmedDatetime) }}</td>
                      <td>{{ statusLabel(item) }}</td>
                      <td>{{ item.lastExportedAt ? fmtDateTime(item.lastExportedAt) : (item.exportStatus === '出力済' ? '—' : '') }}</td>
                      <td>{{ outputLabel(item) }}</td>
                      <td>
                        <v-btn
                          size="small"
                          variant="outlined"
                          @click.prevent="rowOutput(item)"
                          :loading="isOutputting && outputRowKey === voucherKey(item)"
                          :disabled="isOutputting"
                          >{{ $t('resultExport.action.rowOutput') }}</v-btn
                        >
                      </td>
                    </tr>
                  </template>
                  <tr v-if="dataList.length == 0">
                    <td colspan="11" class="text-center text-grey">&nbsp;</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <!-- ページング -->
          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination
                v-model="currentPageNo"
                :length="maxPageNo"
                @update:modelValue="onPageChange"
              />
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    v-model="displayPerPage"
                    :items="displayPerPageItem"
                    item-title="name"
                    item-value="value"
                    :label="$t('common.label.displayCountPerPage')"
                    @update:modelValue="onPerPageChange"
                    return-object/>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  {{ displayContentNo.minNo }} - {{ displayContentNo.maxNo }}{{ $t('common.label.displayedDataRange') }} / {{ totalCount }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- 出力 -->
          <v-row>
            <v-col cols="6" class="text-grey-darken-1">
              {{ $t('resultExport.label.selectedCount', [selected.length]) }}
              <span class="ml-2 text-caption">（{{ $t('resultExport.label.breakdown', [selectedBreakdown.unexported, selectedBreakdown.exported]) }}）</span>
            </v-col>
            <v-col cols="6" class="text-right">
              <!-- 選択解除（ページをまたいだ選択もすべて解除）。2026-10-18 コメント「見出しのチェックボックスと「選択解除」は残す」 -->
              <v-btn
                variant="outlined"
                class="mr-4"
                :disabled="selected.length == 0 || isOutputting"
                @click.prevent="selected = []"
                >{{ $t('resultExport.action.clearSelection') }}</v-btn
              >
              <v-btn
                dark
                class="px-8 bg-green-darken-2"
                elevation="2"
                @click.prevent="bulkOutput"
                :loading="isOutputting && outputRowKey === null"
                :disabled="selected.length == 0 || isOutputting"
                >{{ $t('resultExport.action.output') }}</v-btn
              >
            </v-col>
          </v-row>
          <v-row v-if="errorMessage">
            <v-col cols="12" class="text-right">
              <span style="color: red">{{ errorMessage }}</span>
            </v-col>
          </v-row>
        </v-container>
        <ResultExportConfirmDialog
          v-if="confirm"
          :mode="confirm.mode"
          :reexports="confirm.reexports"
          :unexported-count="confirm.unexportedCount"
          :handle-cancel="closeConfirm"
          :handle-ok="onConfirmOk"
        />
        <ZeroData v-if="zeroDataWarningDialogFlg" :handle-ok="closeZeroDataDialog" />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import DatePicker from "@/components/DatePicker";
import api from "@/apis/staff";
import store from "@/store";
import constant from "@/utils/constant";
import ResultExportConfirmDialog from "@/components/dialog/ResultExportConfirmDialog";
import ZeroData from "@/components/dialog/ZeroDataDialog";

const VOUCHER_TYPES = ["NYUKA", "NYUKA_GACHA", "SHUKKA", "SHUKKA_GACHA", "MOVING"];
const EXPORT_STATUSES = ["ALL", "UNEXPORTED", "EXPORTED"];

export default {
  components: { AppWrapperView, DatePicker, ResultExportConfirmDialog, ZeroData },
  data: () => ({
    siteItem: [],
    dataList: [],
    dayCounts: [], // 日付グループ行の件数（絞込結果全体）
    selected: [],          // 選択された伝票（key form）
    totalCount: 0,
    currentPageNo: 1,
    displayPerPage: {},
    displayPerPageItem: [],
    isSearching: false,
    isOutputting: false,
    outputRowKey: null,   // 行の出力中の伝票キー（まとめて出力中は null）
    confirm: null,        // 再出力の確認 { mode, vouchers, reexports, unexportedCount }
    errorMessage: "",
    zeroDataWarningDialogFlg: false,
    siteFixed: false,     // 店舗ユーザーは自店固定（変更不可）§4.2
    summary: null,
    searchCondition: {
      dateFrom: "",
      dateTo: "",
      siteCodeList: [],
      voucherTypeList: [],
      exportStatus: "ALL",
    },
  }),
  computed: {
    voucherTypeItem() {
      return VOUCHER_TYPES.map(k => ({ key: k, label: this.$t("resultExport.label.voucherTypeItem." + k) }));
    },
    exportStatusItem() {
      return EXPORT_STATUSES.map(k => ({ key: k, label: this.$t("resultExport.label.exportStatusItem." + k) }));
    },
    // 1ページ表示件数。0=「すべて」→ 全件取得（大きなsize）。
    perPageSize() {
      const v = this.displayPerPage ? this.displayPerPage.value : 15;
      return v && v > 0 ? v : 1000000;
    },
    isShowAll() {
      const v = this.displayPerPage ? this.displayPerPage.value : 15;
      return !v || v <= 0;
    },
    maxPageNo() {
      if (this.isShowAll) return this.totalCount > 0 ? 1 : 0;
      return Math.ceil(this.totalCount / this.perPageSize) || 0;
    },
    displayContentNo() {
      if (this.isShowAll) {
        return { minNo: this.totalCount ? 1 : 0, maxNo: this.totalCount };
      }
      const size = this.perPageSize;
      const minNo = this.totalCount != 0 ? 1 + size * (this.currentPageNo - 1) : 0;
      const maxNo = Math.min(size * this.currentPageNo, this.totalCount);
      return { minNo, maxNo };
    },
    // 選択キーのSet（isSelectedをO(1)にして大量行でも軽くする）
    selectedKeySet() {
      const s = new Set();
      for (const x of this.selected) s.add(this.keyOf(x));
      return s;
    },
    isAllSelected() {
      return this.dataList.length > 0 && this.dataList.every(v => this.selectedKeySet.has(this.voucherKey(v)));
    },
    isPartialSelected() {
      return !this.isAllSelected && this.dataList.some(v => this.isSelected(v));
    },
    // 選択件数の内訳（未出力 / 出力済）§4.5
    selectedBreakdown() {
      let exported = 0;
      let unexported = 0;
      for (const s of this.selected) {
        if (s.exported) exported++;
        else unexported++;
      }
      return { exported, unexported };
    },
    // 日付（確定日）ごとのグループ行＋配下の伝票行 §4.4。グループ行に当日の伝票数・未出力数（絞込結果全体。サーバーが集計）を表示。
    groupedRows() {
      const out = [];
      const counts = {};
      for (const c of this.dayCounts) counts[c.day] = { total: c.total, unexported: c.unexported };
      for (const v of this.dataList) {
        const d = this.dayKey(v);
        if (!counts[d]) counts[d] = { total: 0, unexported: 0, _page: true };
        if (counts[d]._page) {
          // サーバー集計が無い場合（通常は発生しない）は表示中の件数
          counts[d].total++;
          if (v.exportStatus !== "出力済") counts[d].unexported++;
        }
      }
      let cur = null;
      for (const v of this.dataList) {
        const d = this.dayKey(v);
        if (d !== cur) {
          cur = d;
          out.push({ _header: true, date: d, total: counts[d].total, unexported: counts[d].unexported });
        }
        out.push(v);
      }
      return out;
    },
  },
  methods: {
    voucherKey(v) {
      return `${v.voucherType}|${v.voucherNo}|${v.siteCode}`;
    },
    keyOf(s) {
      return `${s.voucherType}|${s.voucherNo}|${s.siteCode}`;
    },
    // 選択項目（出力済み判定フラグ付き）
    selItem(v) {
      return {
        voucherType: v.voucherType,
        voucherNo: v.voucherNo,
        siteCode: v.siteCode,
        exported: (v.exportCount || 0) > 0 || v.exportStatus === "出力済",
      };
    },
    voucherTypeLabel(code) {
      return this.$t("resultExport.label.voucherTypeItem." + code);
    },
    // 確定日（グループキー）yyyy/MM/dd。サーバーが設定タイムゾーンで返す確定日（検索の期間と同じ日付）
    dayKey(v) {
      if (v.confirmedDay) return v.confirmedDay;
      if (!v.confirmedDatetime) return "";
      const d = new Date(v.confirmedDatetime);
      if (isNaN(d.getTime())) return "";
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
    },
    // 出力状況ラベル：未出力／出力済／出力済 ×n §4.4
    statusLabel(v) {
      if (v.exportStatus !== "出力済") return v.exportStatus;
      return (v.exportCount && v.exportCount >= 2) ? `出力済 ×${v.exportCount}` : "出力済";
    },
    // 出力者／出力先ラベル：自動（FTP・CSV）／<user>（DL・Excel/CSV、FTP・CSV）／<user>（再出力・DL・Excel/CSV）
    // 最終出力操作の出力先・形式（lastRoutes 例：DOWNLOAD:CSV,DOWNLOAD:XLSX,FTP:CSV）を出力先ごとにまとめる
    outputLabel(v) {
      if (v.exportCount && v.exportCount > 0 && v.lastExportedBy) {
        const who = v.lastExportedBy === "SYSTEM" ? this.$t("resultExport.label.autoBy") : v.lastExportedBy;
        const byDest = { DOWNLOAD: [], FTP: [] };
        for (const r of (v.lastRoutes || "").split(",")) {
          const [dest, fmt] = r.split(":");
          if (byDest[dest]) byDest[dest].push(fmt === "XLSX" ? "Excel" : "CSV");
        }
        const parts = [];
        if (byDest.DOWNLOAD.length) parts.push("DL・" + byDest.DOWNLOAD.sort().join("/"));
        if (byDest.FTP.length) parts.push("FTP・" + byDest.FTP.sort().join("/"));
        const re = v.lastIsReexport ? this.$t("resultExport.label.reexportPrefix") : "";
        return `${who}（${re}${parts.join("、")}）`;
      }
      // フラグのみ（HybridRelay自動／移行前）：履歴なし §7.3
      if (v.exportStatus === "出力済") return this.$t("resultExport.label.autoFtpCsv");
      return "";
    },
    // 既定の期間：当月1日〜当日 §4.2
    setDefaultDates() {
      const now = new Date();
      if (this.$refs.dateFrom) this.$refs.dateFrom.setDate(new Date(now.getFullYear(), now.getMonth(), 1));
      if (this.$refs.dateTo) this.$refs.dateTo.setDate(now);
    },
    fmtDateTime(v) {
      if (!v) return "";
      // ISO(OffsetDateTime) -> yyyy/MM/dd HH:mm:ss
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
    isSelected(v) {
      return this.selectedKeySet.has(this.voucherKey(v));
    },
    toggleRow(v) {
      const k = this.voucherKey(v);
      const idx = this.selected.findIndex(s => this.keyOf(s) == k);
      if (idx >= 0) {
        this.selected.splice(idx, 1);
      } else {
        this.selected.push(this.selItem(v));
      }
    },
    // 表示中の全選択（現ページ）§4.4
    toggleSelectAll(val) {
      if (val) {
        // 現在の選択キーを1回だけ参照（ループ中にcomputedを再計算させない）→ O(n)
        const existing = this.selectedKeySet;
        const add = [];
        for (const v of this.dataList) {
          if (!existing.has(this.voucherKey(v))) {
            add.push(this.selItem(v));
          }
        }
        this.selected = this.selected.concat(add); // 一括代入（reactive通知1回）
      } else {
        const pageKeys = new Set(this.dataList.map(v => this.voucherKey(v)));
        this.selected = this.selected.filter(s => !pageKeys.has(this.keyOf(s)));
      }
    },
    getSiteList() {
      // 自店スコープ付き店舗一覧（非Adminは自店のみ返る）
      api.post("/store/search/minimal", { selectSiteFlag: 1 })
        .then((response) => {
          if (response.status == 1) return;
          (response.data.results?.shopList ?? []).forEach(e => {
            this.siteItem.push({ name: e.name, code: e.code, code_name: e.code + "_" + e.name });
          });
          // 店舗ユーザー（非Admin）は自店固定・変更不可 §4.2：自店を全選択してセレクトを無効化
          if (store.getRole() !== constant.ROLE.ADMIN.id) {
            this.searchCondition.siteCodeList = this.siteItem.map(s => s.code);
            this.siteFixed = true;
          }
        })
        .catch(() => { /* do nothing */ });
    },
    buildRequest(page) {
      return {
        dateFrom: this.searchCondition.dateFrom || "",
        dateTo: this.searchCondition.dateTo || "",
        siteCodeList: this.searchCondition.siteCodeList ?? [],
        voucherTypeList: this.searchCondition.voucherTypeList ?? [],
        exportStatus: this.searchCondition.exportStatus || "ALL",
        page: page,
        size: this.perPageSize,
      };
    },
    search(page) {
      // 処理日From/Toは任意（他実績画面と同様、未指定なら期間条件なし）
      this.isSearching = true;
      this.currentPageNo = page;
      this.errorMessage = "";
      const req = this.buildRequest(page);
      api.post("/result-export/search", req)
        .then((response) => {
          if (response.data?.status == 0) {
            this.dataList = response.data.results?.voucherList ?? [];
            this.dayCounts = response.data.results?.dayCounts ?? [];
            this.totalCount = response.data.results?.totalCount ?? 0;
          } else {
            this.dataList = [];
            this.dayCounts = [];
            this.totalCount = 0;
            // 権限なし・期間の形式不正などはサーバーのメッセージを表示（0件ダイアログにしない）
            this.errorMessage = response.data?.errorMessage || "";
          }
          // 0件ダイアログ処理（他実績画面と同様）。エラー表示中は出さない
          if (this.totalCount == 0 && !this.errorMessage) this.zeroDataWarningDialogFlg = true;
        })
        .catch(() => {
          // エラー時も0件扱い（他実績画面と同様）
          this.dataList = [];
          this.dayCounts = [];
          this.totalCount = 0;
          this.zeroDataWarningDialogFlg = true;
        })
        .finally(() => { this.isSearching = false; });
      // サマリ（件数内訳）
      api.post("/result-export/summary", req)
        .then((response) => {
          this.summary = response.data?.status == 0 ? response.data.results : null;
        })
        .catch(() => { this.summary = null; });
    },
    onPageChange(page) {
      this.search(page);
    },
    onPerPageChange() {
      this.search(1);
    },
    // 検索ボタン：条件が変わるため選択を解除してから検索（表示されない伝票がまとめて出力に残らないように）
    onSearchClick() {
      this.selected = [];
      this.search(1);
    },
    clear() {
      // 条件クリア時も選択を解除する
      this.selected = [];
      this.searchCondition = {
        dateFrom: "",
        dateTo: "",
        siteCodeList: this.siteFixed ? this.siteItem.map(s => s.code) : [],
        voucherTypeList: [],
        exportStatus: "ALL",
      };
      // 期間は既定（当月1日〜当日）に戻す §4.2
      this.setDefaultDates();
    },
    // 行の出力ボタン（1伝票）：ダイアログなしで即実行。出力済の行のみ再出力の確認を表示
    rowOutput(item) {
      this.startOutput("ROW", [this.selItem(item)], this.voucherKey(item));
    },
    // まとめて出力（選択した複数伝票）：ダイアログなしで即実行。出力済を含む場合のみ確認を表示
    bulkOutput() {
      this.startOutput("BULK", this.selected, null);
    },
    startOutput(mode, vouchers, rowKey) {
      this.errorMessage = "";
      this.isOutputting = true;
      this.outputRowKey = rowKey;
      const keys = vouchers.map(v => ({ voucherType: v.voucherType, voucherNo: v.voucherNo, siteCode: v.siteCode }));
      // 出力済かどうかはサーバーで判定（一覧表示後に中継プログラムが出力した場合も確認を出す）
      api.post("/result-export/preview", { vouchers: keys })
        .then((response) => {
          if (response.data?.status != 0 || !response.data.results) {
            this.errorMessage = response.data?.errorMessage || this.$t("common.message.unknownError");
            this.endOutput();
            return;
          }
          const reexports = response.data.results.reexports || [];
          if (reexports.length === 0) {
            this.executeOutput(mode, keys, false, false, rowKey);
            return;
          }
          this.confirm = { mode, vouchers: keys, rowKey, reexports, unexportedCount: keys.length - reexports.length };
        })
        .catch(() => {
          this.errorMessage = this.$t("common.message.unknownError");
          this.endOutput();
        });
    },
    closeConfirm() {
      this.confirm = null;
      this.endOutput();
    },
    onConfirmOk(includeExported) {
      const c = this.confirm;
      this.confirm = null;
      this.executeOutput(c.mode, c.vouchers, includeExported, true, c.rowKey);
    },
    executeOutput(mode, vouchers, includeExported, confirmed, rowKey) {
      const payload = { mode, includeExported, confirmed, vouchers };
      let retry = false;
      api.postWithConfig("/result-export/execute", payload, { responseType: "blob" })
        .then(async (response) => {
          const contentType = response.headers["content-type"] || "";
          if (contentType.indexOf("application/json") < 0) {
            // zipをダウンロード → 選択解除して最新状態を再読込
            this.downloadBlob(response, contentType);
            if (mode === "BULK") this.selected = [];
            this.search(this.currentPageNo);
            return;
          }
          const text = await response.data.text();
          let json = {};
          try { json = JSON.parse(text); } catch (e) { json = {}; }
          if (json.errorCode === "NEED_CONFIRM") {
            // 実行時点で出力済になっていた（一覧表示後に中継プログラムが出力した等）→ 再出力の確認からやり直す
            retry = true;
            return;
          }
          this.errorMessage = json.errorMessage || this.$t("common.message.unknownError");
        })
        .catch(() => {
          this.errorMessage = this.$t("common.message.unknownError");
        })
        .finally(() => {
          this.endOutput();
          if (retry) {
            this.search(this.currentPageNo);
            this.startOutput(mode, vouchers, rowKey);
          }
        });
    },
    endOutput() {
      this.isOutputting = false;
      this.outputRowKey = null;
    },
    downloadBlob(response, contentType) {
      const disposition = response.headers["content-disposition"] || "";
      let filename = "download.zip";
      // filename*=UTF-8''... 優先、無ければ filename="..."
      const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      if (star) {
        filename = decodeURIComponent(star[1]);
      } else {
        const plain = /filename="?([^";]+)"?/i.exec(disposition);
        if (plain) filename = plain[1];
      }
      const blob = new Blob([response.data], { type: contentType || "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    closeZeroDataDialog() {
      this.zeroDataWarningDialogFlg = false;
    },
  },
  created() {
    this.getSiteList();
  },
  mounted() {
    // ページング：既定50件/ページ（spec §4.1）。「すべて」は大量描画になるため任意選択。
    const unit = this.$t("common.label.unitOfRecord");
    this.displayPerPageItem = [
      { name: "15" + unit, value: 15 },
      { name: "50" + unit, value: 50 },
      { name: "100" + unit, value: 100 },
      { name: this.$t("common.label.all"), value: 0 },
    ];
    this.displayPerPage = this.displayPerPageItem[1]; // 既定50
    // 既定の期間：当月1日〜当日 §4.2
    this.setDefaultDates();
  },
};
</script>

<style>
.re-chk {
  width: 18px;
  height: 18px;
  cursor: pointer;
  vertical-align: middle;
}
.re-group td {
  background-color: #f0f3f7;
  font-size: 13px;
  color: #37474f;
}
</style>
